import React, { useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import api from "../../services/api";
import Box from "@mui/material/Box";
import { Button, Dialog, DialogTitle, Typography } from '@mui/material';
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

type Provider = {
  id: number;
  name: string;
  activity_category_id?: number;
};

export const DailyTransferSchedule = ({ date, weekView, minimal, defaultProviderId }: {
  date: any,
  weekView?: any,
  minimal?: boolean,
  defaultProviderId?: number,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [currentDate, setCurrentDate] = useState<string>(String(date).slice(0, 10));
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);

  const [events, setEvents] = useState<any[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const calendarRef = useRef<FullCalendar | null>(null);

  const [providerOptions, setProviderOptions] = useState<Provider[]>([]);
  const [selectedProviders, setSelectedProviders] = useState<Provider[]>([]);
  const [providerFilterTouched, setProviderFilterTouched] = useState(false);

  const COLOR_POOL = useMemo(() => ([
    "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728",
    "#9467bd", "#8c564b", "#e377c2", "#7f7f7f",
    "#bcbd22", "#17becf",
  ]), []);

  const providerColorMapRef = useRef<Map<number, string>>(new Map());

  const toDateOnly = (d: any) => {
    if (!d) return '';
    if (typeof d === 'string') return d.slice(0, 10);
    const dt: Date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toISOString().slice(0, 10);
  };

  const formatHHmm = (d?: Date | string | null) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleTimeString('hu-HU', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Minden provider betöltése,
  // de alapból csak a transfer típusúak legyenek kijelölve
  useEffect(() => {
    api.get(`/service-providers`)
      .then((result: any) => {
        const data = result?.data ?? result ?? [];

        setProviderOptions(data);

        const defaultSelected = (data || []).filter(
          (provider: Provider) => {
            return (defaultProviderId ? (Number(provider.id) === defaultProviderId) : Number(provider.activity_category_id) === 9);
          }
        );

        setSelectedProviders(defaultSelected);
        setProviderFilterTouched(false);
      })
      .catch(() => {
        setProviderOptions([]);
        setSelectedProviders([]);
      });
  }, []);

  useEffect(() => {
    const dateOnly = toDateOnly(date);
    if (!dateOnly) return;

    setCurrentDate(dateOnly);

    if (!calendarRef.current) return;
    const calendarApi = calendarRef.current.getApi();
    calendarApi.gotoDate(dateOnly);
  }, [date, weekView]);

  useEffect(() => {
    if (!range) return;

    if (defaultProviderId && !selectedProviders.length){
      return;
    }

    setLoading(true);

    const searchParams = new URLSearchParams();
    searchParams.set('date_from', range.from);
    searchParams.set('date_to', range.to);
    searchParams.set('provider_filter_touched', providerFilterTouched ? '1' : '0');

    selectedProviders.forEach((p) => {
      searchParams.append('serviceProviderIds[]', String(p.id));
    });

    const url = `/service-reservations/daily-transfers?${searchParams.toString()}`;

    api.get(url)
      .then((result: any) => {
        const data = result?.data ?? result;
        const list = data || [];

        const providerIds: number[] = selectedProviders.length > 0
          ? selectedProviders.map(p => p.id)
          : Array.from(
            new Set(
              list.map((ev: any) => Number(ev.service_provider_id)).filter(Boolean)
            )
          );

        const map = providerColorMapRef.current;

        providerIds.forEach((pid) => {
          if (!map.has(pid)) {
            map.set(pid, COLOR_POOL[map.size % COLOR_POOL.length]);
          }
        });

        const toISO = (dateStr?: string | null, timeStr?: string | null) => {
          if (!dateStr || !timeStr) return null;
          return `${dateStr}T${timeStr}:00`;
        };

        setEvents(
          list
            .filter((ev: any) => ev?.date && ev?.start)
            .map((ev: any) => {
              const pid = Number(ev.service_provider_id);
              const color = map.get(pid) ?? "#2c65de";

              const startIso = toISO(ev.date, ev.start);
              const endIso = ev.end ? toISO(ev.date, ev.end) : null;

              return {
                id: String(ev.id),
                title: ev.title ?? "",
                start: startIso,
                end: endIso,
                backgroundColor: color,
                borderColor: color,
                extendedProps: ev,
              };
            })
        );
      })
      .finally(() => setLoading(false));
  }, [range, selectedProviders, providerFilterTouched, COLOR_POOL]);

  const calendarInitialDate = useMemo(() => {
    return toDateOnly(currentDate);
  }, [currentDate]);

  return (
    <Box sx={{
      '& .fc-toolbar-title': { fontSize: '18px' },
      '& .fc-button.fc-today-button, .fc-button.fc-prev-button, .fc-button.fc-next-button': {
        backgroundColor: '#2c65de',
        border: 'none',
        textTransform: 'uppercase'
      },
      '& .fc-toolbar.fc-header-toolbar': {
        p: '2px',
        m: 0,
        background: minimal ? 'transparent' : '#f1f1f1'
      },
      '& .fc-v-event .fc-event-title': {
        fontSize: '10px'
      },
      opacity: loading ? 0.5 : 1,
      pointerEvents: loading ? 'none' : 'all',
      maxHeight: minimal ? 'calc( 100vh - 330px)' : 'calc(100vh - 200px)',
      height: '100%',
      overflow: 'auto'
    }}>
      <Box
        sx={{
          p: 1,
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgb(0 0 0 / 23%)" },
        }}
      >
        <Autocomplete
          multiple
          fullWidth
          options={providerOptions}
          value={selectedProviders}
          onChange={(_, newValue) => {
            setProviderFilterTouched(true);
            setSelectedProviders(newValue);
          }}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Service providers"
              placeholder="Válassz egyet vagy többet"
              size="small"
            />
          )}
          sx={{
            "& .MuiAutocomplete-inputRoot": {
              maxHeight: 80,
              overflowY: "auto",
              alignItems: "flex-start"
            }
          }}
        />

        <Button
          variant="outlined"
          size="small"
          disabled={providerOptions.length === 0}
          onClick={() => {
            setProviderFilterTouched(true);
            const allSelected = selectedProviders.length === providerOptions.length;
            setSelectedProviders(allSelected ? [] : providerOptions);
          }}
        >
          {selectedProviders.length === providerOptions.length ? "CLEAR" : "ALL"}
        </Button>
      </Box>

      <FullCalendar
        ref={calendarRef}
        initialDate={calendarInitialDate}
        plugins={[timeGridPlugin]}
        initialView={weekView === true ? 'center7Days' : 'timeGridDay'}
        views={{
          center7Days: {
            type: 'timeGrid',
            duration: { days: 8 },
            buttonText: '8 days',
          },
        }}
        allDaySlot={false}
        slotMinTime="00:00:00"
        slotMaxTime="24:00:00"
        events={events}
        height="auto"
        slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
        eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
        datesSet={(arg) => {
          const from = toDateOnly(arg.start);
          const to = toDateOnly(new Date(arg.end.getTime() - 1));

          if (from && to) {
            setRange({ from, to });
          }
        }}
        eventClick={(info) => setSelectedEvent(info.event)}
      />

      {selectedEvent && (
        <Dialog
          open={!!selectedEvent}
          onClose={() => setSelectedEvent(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Event data</DialogTitle>
          <DialogContent dividers>
            <Typography>
              <strong>Service Provider:</strong> {selectedEvent.extendedProps?.service_provider}
            </Typography>

            <Typography>
              <strong>Date:</strong> {selectedEvent.extendedProps?.date}
            </Typography>

            <Typography>
              <strong>Pickup:</strong> {formatHHmm(selectedEvent.start)}
            </Typography>

            <Typography>
              <strong>Start:</strong> {formatHHmm(
              selectedEvent.extendedProps?.date && selectedEvent.extendedProps?.start_time
                ? `${selectedEvent.extendedProps.date}T${selectedEvent.extendedProps.start_time}:00`
                : selectedEvent.extendedProps?.start_time
            )}
            </Typography>

            <Typography>
              <strong>End:</strong> {formatHHmm(selectedEvent.end)}
            </Typography>

            <Typography>
              <strong>Group:</strong> {selectedEvent.extendedProps?.group}
            </Typography>

            <Typography>
              <strong>Activity:</strong> {selectedEvent.extendedProps?.activity}
            </Typography>

            <Typography>
              <strong>Service:</strong> {selectedEvent.extendedProps?.service}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedEvent(null)}>Bezár</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};